import { useEffect, useState, useMemo } from 'react';
import { api } from '../App';
import { toast } from 'sonner';
import { formatDate } from '../utils/dateFormat';
import { DateInput } from '../components/DateInput';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Pencil, Trash, MagnifyingGlass, Key, Copy, Eye, EyeSlash, EnvelopeSimple, WarningCircle, CheckCircle } from '@phosphor-icons/react';
import { confirmDialog } from '@/components/ConfirmDialog';
import ClientAutocomplete from '../components/ClientAutocomplete';

const EMPTY = { client_id: '', password: '', datum_isteka: '', napomena: '' };

export default function TokeniPage() {
  const [tokeni, setTokeni] = useState([]);
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [visibleIds, setVisibleIds] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const [t, c] = await Promise.all([
        api.get('/tokeni'),
        api.get('/clients'),
      ]);
      setTokeni(t.data || []);
      setClients(c.data || []);
    } catch (e) {
      toast.error('Greska pri ucitavanju');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = (search || '').toLowerCase().trim();
    if (!q) return tokeni;
    return tokeni.filter(t =>
      (t.client_naziv || '').toLowerCase().includes(q) ||
      (t.client_email || '').toLowerCase().includes(q) ||
      (t.napomena || '').toLowerCase().includes(q)
    );
  }, [tokeni, search]);

  const handleSave = async () => {
    if (!form.client_id) return toast.error('Izaberite klijenta');
    if (!form.password) return toast.error('Unesite password');
    if (!form.datum_isteka) return toast.error('Unesite datum isteka');

    try {
      if (editingId) {
        // Only send mutable fields in update
        await api.put(`/tokeni/${editingId}`, {
          password: form.password,
          datum_isteka: form.datum_isteka,
          napomena: form.napomena || '',
        });
        toast.success('Token azuriran');
      } else {
        await api.post('/tokeni', form);
        toast.success('Token kreiran');
      }
      setOpen(false);
      setForm(EMPTY);
      setEditingId(null);
      load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map(d => d.msg || d).join(', ') : (detail || e.message || 'Greska pri cuvanju');
      toast.error(msg);
    }
  };

  const handleEdit = (t) => {
    setEditingId(t.id);
    setForm({
      client_id: t.client_id,
      password: t.password,
      datum_isteka: t.datum_isteka,
      napomena: t.napomena || '',
    });
    setOpen(true);
  };

  const handleDelete = async (id) => {
    if (!await confirmDialog({ title: 'Brisanje tokena', message: 'Da li ste sigurni?', confirmText: 'Obrisi', variant: 'danger' })) return;
    try {
      await api.delete(`/tokeni/${id}`);
      toast.success('Token obrisan');
      load();
    } catch (e) {
      toast.error('Greska pri brisanju');
    }
  };

  const copyPassword = async (pwd, id) => {
    try {
      await navigator.clipboard.writeText(pwd);
      toast.success('Password kopiran u clipboard');
    } catch {
      // fallback
      const ta = document.createElement('textarea');
      ta.value = pwd;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); toast.success('Password kopiran'); } catch { toast.error('Ne mogu kopirati'); }
      ta.remove();
    }
    // Track copy count (fire-and-forget), then reload to re-sort
    if (id) {
      try {
        await api.post(`/tokeni/${id}/copy`);
        load();
      } catch {}
    }
  };

  const toggleVisible = (id) => setVisibleIds(prev => ({ ...prev, [id]: !prev[id] }));

  const sendNotification = async (id, clientName) => {
    if (!await confirmDialog({ title: 'Obavjestenje', message: `Poslati obavjestenje o isteku tokena klijentu "${clientName}"?`, confirmText: 'Posalji' })) return;
    try {
      const r = await api.post(`/tokeni/${id}/notify`);
      toast.success(r.data?.message || 'Email poslat');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Greska pri slanju');
    }
  };

  const getExpiryBadge = (dana) => {
    if (dana === null || dana === undefined) return null;
    if (dana < 0) return <span className="px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700 border border-red-200">Istekao ({Math.abs(dana)}d)</span>;
    if (dana <= 10) return <span className="px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800 border border-amber-200">Istice za {dana}d</span>;
    if (dana <= 30) return <span className="px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">{dana}d</span>;
    return <span className="px-2 py-0.5 rounded text-xs font-medium bg-green-50 text-green-700 border border-green-200">{dana}d</span>;
  };

  return (
    <div className="space-y-6" data-testid="tokeni-page">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-slate-900 flex items-center gap-2">
            <Key size={22} weight="duotone" className="text-indigo-600" />
            Tokeni klijenata
          </h3>
          <p className="text-sm text-slate-500 mt-1">Pristupni podaci + automatska obavjestenja 10 dana prije isteka</p>
        </div>
        <Button onClick={() => { setForm(EMPTY); setEditingId(null); setOpen(true); }} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="tokeni-new-btn">
          <Plus size={16} className="mr-2" /> Novi token
        </Button>
      </div>

      <Card className="p-4 bg-white border-slate-200">
        <div className="relative max-w-md">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Brza pretraga po klijentu, emailu ili napomeni..."
            className="pl-9 bg-white border-slate-300"
            data-testid="tokeni-search"
          />
        </div>
      </Card>

      <Card className="overflow-hidden bg-white border-slate-200">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-xs font-semibold tracking-wider uppercase text-slate-600">
                <th className="px-4 py-3">Klijent</th>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Password</th>
                <th className="px-4 py-3">Datum isteka</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Napomena</th>
                <th className="px-4 py-3 text-right">Akcije</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={7} className="px-4 py-6 text-center text-slate-400">Ucitavanje...</td></tr>}
              {!loading && filtered.length === 0 && <tr><td colSpan={7} className="px-4 py-6 text-center text-slate-400">Nema tokena</td></tr>}
              {filtered.map((t) => (
                <tr key={t.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`token-row-${t.id}`}>
                  <td className="px-4 py-3 font-medium text-slate-900">{t.client_naziv || '-'}</td>
                  <td className="px-4 py-3 text-slate-600 text-xs">{t.client_email || '-'}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      <code className="bg-slate-100 px-2 py-1 rounded text-xs font-mono min-w-[120px] inline-block">
                        {visibleIds[t.id] ? t.password : '•'.repeat(Math.min((t.password || '').length, 12))}
                      </code>
                      <button onClick={() => toggleVisible(t.id)} className="p-1.5 hover:bg-slate-200 rounded" title={visibleIds[t.id] ? 'Sakrij' : 'Prikazi'} data-testid={`token-toggle-${t.id}`}>
                        {visibleIds[t.id] ? <EyeSlash size={14} className="text-slate-600" /> : <Eye size={14} className="text-slate-600" />}
                      </button>
                      <button onClick={() => copyPassword(t.password, t.id)} className="flex items-center gap-1 px-2 py-1 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 rounded text-xs font-medium text-indigo-700 transition" title="Kopiraj password" data-testid={`token-copy-${t.id}`}>
                        <Copy size={13} weight="duotone" /> Kopiraj
                      </button>
                    </div>
                  </td>
                  <td className="px-4 py-3 font-medium text-slate-900 whitespace-nowrap">{formatDate(t.datum_isteka)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      {getExpiryBadge(t.dana_do_isteka)}
                      {t.notified_10d && <span title={`Obavjesten: ${t.notified_10d_at ? formatDate(t.notified_10d_at) : ''}`}><CheckCircle size={14} weight="duotone" className="text-green-600" /></span>}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-600 text-xs max-w-[200px] truncate" title={t.napomena}>{t.napomena || '-'}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => sendNotification(t.id, t.client_naziv)} className="p-2 hover:bg-blue-100 rounded" title="Posalji obavjestenje odmah" data-testid={`token-notify-${t.id}`}>
                        <EnvelopeSimple size={14} className="text-blue-600" />
                      </button>
                      <button onClick={() => handleEdit(t)} className="p-2 hover:bg-slate-200 rounded" title="Uredi" data-testid={`token-edit-${t.id}`}>
                        <Pencil size={14} className="text-slate-600" />
                      </button>
                      <button onClick={() => handleDelete(t.id)} className="p-2 hover:bg-red-100 rounded" title="Obrisi" data-testid={`token-delete-${t.id}`}>
                        <Trash size={14} className="text-red-600" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="tokeni-dialog">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Uredi token' : 'Novi token'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs font-semibold uppercase text-slate-500">Klijent *</Label>
              <ClientAutocomplete clients={clients} value={form.client_id} onChange={(id) => setForm({ ...form, client_id: id })} testid="tokeni-client-select" placeholder="Pretraga po nazivu ili PIB-u..." />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase text-slate-500">Password *</Label>
              <div className="flex gap-2">
                <Input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="bg-white border-slate-300 font-mono" data-testid="tokeni-password" />
                <Button type="button" variant="outline" onClick={() => copyPassword(form.password)} disabled={!form.password} className="shrink-0 border-indigo-300 text-indigo-700 hover:bg-indigo-50" data-testid="tokeni-dialog-copy">
                  <Copy size={14} className="mr-1" /> Kopiraj
                </Button>
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase text-slate-500">Datum isteka tokena *</Label>
              <DateInput value={form.datum_isteka} onChange={(e) => setForm({ ...form, datum_isteka: e.target.value })} className="border-slate-300" />
              <p className="text-xs text-slate-400 mt-1">Sistem ce automatski poslati obavjestenje 10 dana prije ovog datuma.</p>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase text-slate-500">Napomena</Label>
              <Input value={form.napomena} onChange={(e) => setForm({ ...form, napomena: e.target.value })} className="bg-white border-slate-300" placeholder="Opcionalno" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setOpen(false)}>Otkazi</Button>
              <Button onClick={handleSave} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="tokeni-save">
                {editingId ? 'Sacuvaj' : 'Kreiraj'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
